import React, { useState, useEffect } from 'react';
import { ArrowPathIcon, CloudArrowDownIcon } from '@heroicons/react/24/outline';
import FeedStatusTable from '../components/FeedStatus/FeedStatusTable';
import FeedStatusFilters from '../components/FeedStatus/FeedStatusFilters';
import ApiClient from '../utils/ApiClient';

const FeedStatus = () => {
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedFeeds, setSelectedFeeds] = useState(new Set());
  const [filters, setFilters] = useState({
    status: '',
    feedType: '',
    startDate: '',
    endDate: '',
    createdBy: ''
  });

  const fetchFeeds = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await ApiClient.get(`/api/feeds/status?page=${page}&size=${rowsPerPage}`);
      setFeeds(response.data || []);
    } catch (err) {
      setError('Failed to fetch feed statuses');
      console.error('Error fetching feeds:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeeds();
  }, [page, rowsPerPage]);

  const handleRefresh = () => {
    fetchFeeds();
  };

  const handleManualCheck = async () => {
    if (selectedFeeds.size === 0) {
      setError('Please select at least one feed to check');
      return;
    }
    
    setRefreshing(true);
    setError(null);
    
    try {
      const feedIds = Array.from(selectedFeeds);
      await ApiClient.post('/api/feeds/check-selected', { feedIds });
      
      setTimeout(() => {
        fetchFeeds();
        setSelectedFeeds(new Set()); // Clear selection after check
        setRefreshing(false);
      }, 2000);
    } catch (err) {
      setError('Failed to trigger feed check');
      setRefreshing(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPage(0);
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleSelectFeed = (feedId) => {
    const newSelected = new Set(selectedFeeds);
    if (newSelected.has(feedId)) {
      newSelected.delete(feedId);
    } else {
      newSelected.add(feedId);
    }
    setSelectedFeeds(newSelected);
  };

  const handleSelectAll = (feedIds) => {
    if (selectedFeeds.size === feedIds.length) {
      setSelectedFeeds(new Set());
    } else {
      setSelectedFeeds(new Set(feedIds));
    }
  };

  // Filter feeds based on criteria
  const filteredFeeds = feeds.filter(feed => {
    if (filters.status && feed.status !== filters.status) return false;
    if (filters.feedType && feed.feed_type !== filters.feedType) return false;
    if (filters.createdBy && !feed.created_by?.toLowerCase().includes(filters.createdBy.toLowerCase())) return false;
    if (filters.startDate && new Date(feed.submitted_at) < new Date(filters.startDate)) return false;
    if (filters.endDate && new Date(feed.submitted_at) > new Date(filters.endDate)) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="md:flex md:items-center md:justify-between mb-6">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
              Amazon Feed Status
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Monitor and track the status of Amazon feed submissions
            </p>
          </div>
          <div className="mt-4 flex md:mt-0 md:ml-4 space-x-3">
            <button
              type="button"
              onClick={handleRefresh}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <ArrowPathIcon className="-ml-1 mr-2 h-5 w-5 text-gray-500" />
              Refresh
            </button>
            <button
              type="button"
              onClick={handleManualCheck}
              disabled={refreshing || selectedFeeds.size === 0}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CloudArrowDownIcon className="-ml-1 mr-2 h-5 w-5" />
              {refreshing ? 'Checking...' : selectedFeeds.size > 0 ? `Check ${selectedFeeds.size} Selected` : 'Check Feed Status'}
            </button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <FeedStatusFilters 
          filters={filters}
          onFilterChange={handleFilterChange}
        />

        {/* Table */}
        <div className="mt-6">
          <FeedStatusTable
            feeds={filteredFeeds}
            loading={loading}
            page={page}
            rowsPerPage={rowsPerPage}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            selectedFeeds={selectedFeeds}
            onSelectFeed={handleSelectFeed}
            onSelectAll={handleSelectAll}
          />
        </div>
      </div>
    </div>
  );
};

export default FeedStatus;